import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Editor } from '@tiptap/react';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import type { Collection } from '../components/CollectionsPanel';
import {
  ABOUT_PHOTO_DRAG,
  AboutEditorContent,
  AboutPhotosContext,
  AboutToolbar,
  loadContent,
  useAboutEditor,
  type EditorPhoto,
} from '../components/AboutEditor';

interface AboutPage {
  html: string;
  updated_at: string | null;
  updated_by: string | null;
}

const STOREFRONT_ABOUT_URL = 'https://thisplaceisyourspace.com/about';

// ──────────────────────────────────────────────────────────────────────────────
// Draft recovery: unsaved edits are mirrored to this browser's localStorage so
// a refresh, crash or closed tab doesn't lose them. Best-effort only — storage
// can be unavailable (private mode, blocked site data), and then nothing is kept.
// ──────────────────────────────────────────────────────────────────────────────

const DRAFT_KEY = 'lumina-admin:about-draft:v1';

interface Draft {
  html: string;
  /** updated_at of the saved page the edits started from. */
  base: string | null;
  savedAt: number;
}

function readDraft(): Draft | null {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null');
    return d && typeof d.html === 'string' && typeof d.savedAt === 'number' ? d : null;
  } catch {
    return null;
  }
}

function writeDraft(d: Draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {}
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {}
}

export function AboutEdit() {
  const [page, setPage] = useState<AboutPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [allPhotos, setAllPhotos] = useState<EditorPhoto[]>([]);

  useEffect(() => {
    api
      .get<{ page: AboutPage }>('/api/admin/about')
      .then((d) => setPage(d.page))
      .catch((err) => setError(err.message));
    api
      .get<{ collections: Collection[] }>('/api/admin/collections')
      .then((d) => setCollection(d.collections.find((c) => c.system_key === 'about') ?? null))
      .catch(console.error);
    api
      .get<{ photos: EditorPhoto[] }>('/api/admin/photos')
      .then((d) => setAllPhotos(d.photos))
      .catch(console.error);
  }, []);

  // Only the About collection's photos can be placed (and render on the site).
  const photos = useMemo(() => {
    const byUuid = new Map(allPhotos.map((p) => [p.uuid, p]));
    return new Map(
      (collection?.photo_uuids ?? []).flatMap((u) => (byUuid.has(u) ? [[u, byUuid.get(u)!] as const] : []))
    );
  }, [allPhotos, collection]);

  if (!page) return <p>{error ?? 'Loading…'}</p>;
  return <AboutEditorPage initial={page} photos={photos} collectionLoaded={!!collection} />;
}

function AboutEditorPage({
  initial,
  photos,
  collectionLoaded,
}: {
  initial: AboutPage;
  photos: Map<string, EditorPhoto>;
  collectionLoaded: boolean;
}) {
  const [page, setPage] = useState(initial);
  const pageRef = useRef(page);
  pageRef.current = page;
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [recovered, setRecovered] = useState<{ savedAt: number; stale: boolean } | null>(null);
  // The saved page as the editor serializes it, to tell real edits from none.
  const baseline = useRef('');
  const pendingDraft = useRef<number | null>(null);

  const flushDraft = (editor: Editor) => {
    if (pendingDraft.current !== null) window.clearTimeout(pendingDraft.current);
    pendingDraft.current = null;
    const html = editor.getHTML();
    if (html === baseline.current) clearDraft();
    else writeDraft({ html, base: pageRef.current.updated_at, savedAt: Date.now() });
  };

  const editor = useAboutEditor({
    content: initial.html,
    onEdit: (e) => {
      // Undoing back to the saved version isn't an unsaved change.
      const changed = e.getHTML() !== baseline.current;
      setDirty(changed);
      if (pendingDraft.current !== null) window.clearTimeout(pendingDraft.current);
      pendingDraft.current = window.setTimeout(() => flushDraft(e), 300);
    },
    onLinkShortcut: () => setLinkOpen(true),
  });

  // Restore a draft left by a refresh or crash, if it differs from the saved page.
  useEffect(() => {
    if (!editor) return;
    baseline.current = editor.getHTML();
    const draft = readDraft();
    if (!draft) return;
    if (draft.html === baseline.current) return clearDraft();
    // Deferred: node views render synchronously, which React refuses mid-commit.
    queueMicrotask(() => {
      loadContent(editor, draft.html);
      setDirty(true);
      setRecovered({ savedAt: draft.savedAt, stale: draft.base !== initial.updated_at });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // The debounce could lose the last keystrokes on close; write them now.
  useEffect(() => {
    if (!editor) return;
    const flush = () => {
      if (pendingDraft.current !== null) flushDraft(editor);
    };
    const onHide = () => document.visibilityState === 'hidden' && flush();
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      flush(); // navigating elsewhere in the admin
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (!editor) return null;

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const html = editor.getHTML();
      const d = await api.put<{ page: AboutPage }>('/api/admin/about', { html });
      setPage(d.page);
      // The cursor stays put: the editor keeps its content, which now counts as saved.
      baseline.current = html;
      if (pendingDraft.current !== null) window.clearTimeout(pendingDraft.current);
      pendingDraft.current = null;
      clearDraft();
      setDirty(editor.getHTML() !== html); // typed during the save → still unsaved
      setRecovered(null);
      setMessage({ text: 'Saved. The About page is live.', error: false });
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    if (!window.confirm('Discard your unsaved changes and go back to the saved page?')) return;
    if (pendingDraft.current !== null) window.clearTimeout(pendingDraft.current);
    pendingDraft.current = null;
    loadContent(editor, page.html);
    baseline.current = editor.getHTML();
    clearDraft();
    setDirty(false);
    setRecovered(null);
    setMessage(null);
  };

  return (
    <div
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          if (dirty && !busy) save();
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: 0 }}>About page</h1>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            {page.updated_at
              ? `Last saved ${formatDate(page.updated_at)}${page.updated_by ? ` by ${page.updated_by}` : ''} · `
              : 'Not published yet · '}
            <a href={STOREFRONT_ABOUT_URL} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>
              View page
            </a>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {message ? (
            <span style={{ fontSize: 13, color: message.error ? '#991b1b' : '#166534' }}>{message.text}</span>
          ) : (
            dirty && <span style={{ fontSize: 13, color: '#6b7280' }}>Unsaved changes · draft kept in this browser</span>
          )}
          {dirty && (
            <button type="button" onClick={discard} style={secondaryButton}>
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            title={`Save (${/Mac/.test(navigator.platform) ? '⌘' : 'Ctrl+'}S)`}
            style={{
              padding: '10px 16px',
              background: '#1a1a1a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              cursor: busy || !dirty ? 'default' : 'pointer',
              opacity: busy || !dirty ? 0.5 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {recovered && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 16,
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            background: recovered.stale ? '#fef3c7' : '#eff6ff',
            border: `1px solid ${recovered.stale ? '#fcd34d' : '#bfdbfe'}`,
            color: recovered.stale ? '#92400e' : '#1e3a8a',
          }}
        >
          <span style={{ flex: 1 }}>
            Restored unsaved changes from {new Date(recovered.savedAt).toLocaleString()}.{' '}
            {recovered.stale
              ? 'The page has been saved since these edits began — saving will replace that version.'
              : 'Save to publish them, or discard to go back to the saved page.'}
          </span>
          <button type="button" onClick={discard} style={secondaryButton}>
            Discard recovered changes
          </button>
          <button type="button" onClick={() => setRecovered(null)} style={{ ...secondaryButton, border: 'none', background: 'transparent' }}>
            Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginTop: 16 }}>
        <div style={{ flex: 3, minWidth: 0 }}>
          <AboutToolbar editor={editor} linkOpen={linkOpen} setLinkOpen={setLinkOpen} />
          <AboutPhotosContext.Provider value={photos}>
            <AboutEditorContent editor={editor} />
          </AboutPhotosContext.Provider>
        </div>

        <aside
          style={{
            flex: 1,
            minWidth: 240,
            position: 'sticky',
            top: 24,
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500 }}>About collection</div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 12px', lineHeight: 1.5 }}>
            Drag a photo into the text, or click to add it at the cursor. Click a placed photo to align or
            remove it; drag it to move it.
          </p>
          {photos.size === 0 ? (
            <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
              {collectionLoaded ? 'No photos in the About collection yet.' : 'Loading…'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8 }}>
              {[...photos.values()].map((p) => (
                <button
                  key={p.uuid}
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(ABOUT_PHOTO_DRAG, p.uuid);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  // No mousedown preventDefault here: it would stop the drag. focus()
                  // puts the cursor back where it was.
                  onClick={() => editor.chain().focus().insertAboutPhoto(p.uuid).run()}
                  title={p.title}
                  style={{
                    padding: 0,
                    border: 'none',
                    aspectRatio: '1',
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: '#f3f4f6',
                    cursor: 'grab',
                  }}
                >
                  <img
                    src={p.thumb_url}
                    alt={p.title}
                    draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          )}
          <Link to="/photos" style={{ display: 'block', marginTop: 12, fontSize: 12, color: '#6b7280', textDecoration: 'underline' }}>
            Add or remove photos on the Photos page
          </Link>
        </aside>
      </div>
    </div>
  );
}

const secondaryButton: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  color: 'inherit',
  whiteSpace: 'nowrap',
};
